import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoPlusCircle } from "react-icons/go";
import { FiMinusCircle } from "react-icons/fi";
import { API_URL } from "../../Config";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// "2026-02-28" (or "2026-02-28T00:00:00.000Z") -> "28 February 2026"
const formatDateLong = (date) => {
  if (!date) return "";
  const [year, month, day] = String(date).split("T")[0].split("-");
  if (!year || !month || !day) return date;
  return `${parseInt(day, 10)} ${MONTHS[parseInt(month, 10) - 1]} ${year}`;
};

// Date picker that never changes the input's type on focus (that trick crashes
// Safari). A real <input type="date"> sits invisibly on top of a formatted label,
// so the picker opens natively and the saved value stays "YYYY-MM-DD".
const DateField = ({
  value,
  display,
  placeholder,
  className,
  onChange,
  min,
  wrapperClassName = "w-full min-w-0",
}) => (
  <div className={`relative ${wrapperClassName}`}>
    <span
      className={`block truncate ${className} ${value ? "" : "text-[#b9bdb6]"}`}
    >
      {display || placeholder}
    </span>
    <input
      type="date"
      value={value || ""}
      min={min || undefined}
      onChange={onChange}
      onClick={(e) => {
        try {
          if (e.target.showPicker) e.target.showPicker();
        } catch (_) {
          /* picker already open or unsupported */
        }
      }}
      className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
    />
  </div>
);

// ₹ sign + amount that hug each other: the input is only as wide as its value,
// so the symbol never drifts away from the number.
const MoneyField = ({
  value,
  onChange,
  className,
  type = "text",
  placeholder = "0000",
}) => (
  <span className="inline-flex items-center justify-end">
    <span>₹</span>
    <input
      type={type}
      className={`${className} text-right`}
      style={{ width: `${Math.max(String(value ?? "").length, 4) + 1}ch` }}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
    />
  </span>
);

const emptyRow = () => ({
  from: "",
  to: "",
  particulars: "",
  nights: "",
  amount: "",
  cashback: "",
});

const CashbackElementore = () => {
  const navigate = useNavigate();

  const [invoiceno, setinvoiceno] = useState("");
  const [invoicedate, setinvoicedate] = useState("");
  const [guestname, setguestname] = useState("");
  const [gstno, setgstno] = useState("");
  const [country, setcountry] = useState("");
  const [amountinwords, setamountinwords] = useState("");
  const [total, setTotal] = useState("");
  const [rows, setRows] = useState([emptyRow()]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDateToDisplay = (date) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    if (!year || !month || !day) return date;
    return `${day}-${month}-${year}`;
  };
  // "2026-11-07" -> "2026-11-08" (string-based, so no timezone shifts)
  const nextDay = (date) => {
    if (!date) return "";
    const [y, m, d] = String(date).split("T")[0].split("-").map(Number);
    if (!y || !m || !d) return "";
    return new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  };
  const addRow = () => {
    if (rows.length < 7) {
      setRows([...rows, emptyRow()]);
    }
  };

  const removeRow = (indexToRemove) => {
    setRows(rows.filter((_, index) => index !== indexToRemove));
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    if (
      field === "from" &&
      updatedRows[index].to &&
      updatedRows[index].to <= value
    ) {
      updatedRows[index].to = "";
    }
    setRows(updatedRows);
  };

  const handleCancel = () => {
    const confirmed = window.confirm("Sure want to discard changes?");
    if (confirmed) {
      navigate("/Bills");
    }
  };

  const handleSubmit = async () => {
    if (!invoiceno || !invoicedate || !guestname) {
      alert("Invoice No, Invoice Date and Guest Name are required.");
      return;
    }
    if (rows.length === 0) {
      alert("Cashback invoice must contain at least one item.");
      return;
    }

    const payload = {
      invoiceno,
      invoicedate,
      guestname,
      gstno,
      country,
      amountinwords,
      total: total || 0,
      items: rows.map((row) => ({
        particulars: row.particulars,
        checkin: row.from || null,
        checkout: row.to || null,
        nights: row.nights || 0,
        amount: row.amount || 0,
        cashback: row.cashback || 0,
      })),
    };

    try {
      setIsSubmitting(true);
      const res = await fetch(`${API_URL}/billing/cashback-invoice`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        alert(data.message || "Failed to save cashback invoice.");
        return;
      }

      alert("Cashback invoice saved successfully!");
      navigate("/bills");
    } catch (err) {
      console.error("Error saving cashback invoice:", err);
      alert("Something went wrong while saving the cashback invoice.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Shared input style (visual only)
  const bare =
    "w-full p-0 bg-transparent outline-none border-none focus:ring-0 focus:outline-none placeholder:text-[#b9bdb6] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";
  const label = "text-[10px] tracking-wide text-[#7b807a] uppercase";

  // grows the textarea to fit its content so long names wrap instead of being cut
  const autoResize = (el) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  };

  return (
    <div className="cb-invoice max-w-4xl mx-auto px-12 py-12 bg-white text-[#1c2420] print:p-0">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap');
        .cb-invoice { font-family: 'Montserrat', ui-sans-serif, system-ui, sans-serif; }
        .cb-title { font-weight: 500; letter-spacing: -0.04em; line-height: 0.88; }
        .cb-invoice input, .cb-invoice textarea { font-family: inherit; }
        .cb-invoice table input, .cb-invoice table textarea { font-size: inherit; }
        @media print {
          @page { margin: 0; }
          body { margin: 1.6cm; }
          .no-print { display: none; }
        }
      `}</style>

      {/* Header */}
      <div className="flex justify-between items-start">
        <h1 className="cb-title text-[76px] uppercase">
          Cashback
          <br />
          Invoice
        </h1>
        <div className="w-28 h-28 shrink-0">
          <img
            src="https://zorbastays.com/android-chrome-512x512.png"
            alt="Zorba Stays Logo"
            className="w-full h-full object-contain"
          />
        </div>
      </div>

      {/* Invoice Meta */}
      <div className="flex gap-16 mt-10 pb-6 border-b border-[#1c2420]">
        <div>
          <p className={label}>Invoice No.</p>
          <input
            type="text"
            className={`${bare} w-24 mt-1 text-sm font-bold`}
            placeholder="00"
            value={invoiceno}
            onChange={(e) => setinvoiceno(e.target.value)}
          />
        </div>
        <div>
          <p className={label}>Date</p>
          <DateField
            className={`${bare} mt-1 text-sm font-bold`}
            placeholder="Select date"
            display={formatDateLong(invoicedate)}
            value={invoicedate}
            onChange={(e) => setinvoicedate(e.target.value)}
          />
        </div>
      </div>

      {/* Bill To / From */}
      <div className="flex justify-between items-start py-6 border-b border-[#1c2420]">
        <div className="w-1/2">
          <p className={label}>Bill To</p>
          <input
            type="text"
            className={`${bare} mt-3 text-base font-bold`}
            placeholder="Guest Name"
            value={guestname}
            onChange={(e) => setguestname(e.target.value)}
          />
          <div className="mt-1 flex items-center gap-1 text-xs tracking-wider text-[#3b413a]">
            <span className="shrink-0">GSTIN NO.:</span>
            <input
              type="text"
              maxLength={15}
              style={{ width: "auto" }}
              className={`${bare} flex-1 min-w-0 uppercase text-xs`}
              placeholder="15 character GSTIN"
              value={gstno}
              onChange={(e) =>
                setgstno(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))
              }
            />
          </div>
          <textarea
            className={`${bare}  mt-1 text-xs tracking-wider text-[#3b413a] resize-none`}
            placeholder="Enter Address"
            value={country}
            onChange={(e) => setcountry(e.target.value)}
            rows={6}
            maxLength={350}
          />
        </div>
        <div className="text-right text-xs tracking-wider leading-[1.9] text-[#3b413a]">
          <p className={label}>From</p>
          <p className="mt-3 text-base font-bold text-[#1c2420] leading-normal">
            Zorba Stays
          </p>
          <p>GSTIN NO.: 24AADFZ2976K1ZO</p>
          <p>306-7 - Business Centrum,</p>
          <p>Amin Marg,</p>
          <p>Rajkot, 360001</p>
          <p>India</p>
        </div>
      </div>

      {/* Table */}
      <table className="w-full border-collapse text-xs mt-8">
        <thead>
          <tr className="text-[10px] uppercase text-[#7b807a] text-left align-bottom">
            <th className="pb-4 font-normal w-[50px]">
              Sr.
              {/* <br /> */}
              No.
            </th>
            <th className="pb-4 font-normal w-[34%]">Particulars</th>
            <th className="pb-4 font-normal w-[26%]">Period of stay</th>
            <th className="pb-4 font-normal text-center">No. of nights</th>
            <th className="pb-4 font-normal text-center">Bill amount</th>
            <th className="pb-4 font-normal text-right">Cashback</th>
            <th className="pb-4 w-8 no-print"></th>
          </tr>
        </thead>
        <tbody className="border-t border-[#1c2420]">
          {rows.map((row, index) => (
            <tr key={index} className="border-b border-[#1c2420] align-middle">
              <td className="py-4">{index + 1}</td>
              <td className="py-4 pr-2">
                <textarea
                  rows={1}
                  ref={autoResize}
                  className={`${bare} block resize-none overflow-hidden leading-relaxed`}
                  placeholder="Villa Name"
                  value={row.particulars}
                  onChange={(e) =>
                    handleInputChange(index, "particulars", e.target.value)
                  }
                />
              </td>
              <td className="py-4 pr-2">
                <div className="flex items-center gap-2">
                  <DateField
                    wrapperClassName="w-[4.2rem] shrink-0"
                    className={`${bare} min-w-0 text-left`}
                    placeholder="DD-MM-YYYY"
                    display={formatDateToDisplay(row.from)}
                    value={row.from}
                    onChange={(e) =>
                      handleInputChange(index, "from", e.target.value)
                    }
                  />
                  <span className="mx-1">-</span>
                  <DateField
                    wrapperClassName="w-[4.2rem] shrink-0"
                    className={`${bare} min-w-0 text-left`}
                    placeholder="DD-MM-YYYY"
                    display={formatDateToDisplay(row.to)}
                    value={row.to}
                    min={nextDay(row.from)}
                    onChange={(e) => {
                      if (row.from && e.target.value <= row.from) return;
                      handleInputChange(index, "to", e.target.value);
                    }}
                  />
                </div>
              </td>
              <td className="py-4">
                <input
                  type="text"
                  className={`${bare} text-center`}
                  placeholder="00"
                  value={row.nights}
                  onChange={(e) =>
                    handleInputChange(index, "nights", e.target.value)
                  }
                />
              </td>
              <td className="py-4 pl-6">
                <div className="flex items-center justify-center">
                  <MoneyField
                    type="text"
                    className={`${bare}`}
                    value={row.amount}
                    onChange={(e) =>
                      handleInputChange(index, "amount", e.target.value)
                    }
                  />
                </div>
              </td>
              <td className="py-4 pl-6">
                <div className="flex items-center justify-end">
                  <MoneyField
                    type="text"
                    className={`${bare}`}
                    value={row.cashback}
                    onChange={(e) =>
                      handleInputChange(index, "cashback", e.target.value)
                    }
                  />
                </div>
              </td>
              <td className="py-4 no-print text-right">
                {index >= 1 && (
                  <button
                    onClick={() => removeRow(index)}
                    className="px-2 py-1 hover:scale-110"
                  >
                    <FiMinusCircle size={18} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Add Button */}
      <div className="my-2 no-print">
        <button
          onClick={addRow}
          disabled={rows.length >= 7}
          className={`py-2 ${rows.length >= 7 ? "opacity-50 cursor-not-allowed" : "hover:scale-110"}`}
        >
          <GoPlusCircle size={22} />
        </button>
      </div>

      {/* Total */}
      <div className="flex justify-end items-center py-4 border-y border-[#1c2420] text-xs font-bold">
        <span className="mr-24">Total</span>
        <div className="flex items-center justify-end w-28">
          <MoneyField
            type="number"
            className={`${bare} font-bold`}
            value={total}
            onChange={(e) => setTotal(e.target.value)}
          />
        </div>
      </div>

      {/* In Words */}
      <div className="mt-8">
        <p className={label}>In Words</p>
        <input
          type="text"
          className={`${bare} mt-1 text-sm font-bold`}
          placeholder="Enter amount in words"
          value={amountinwords}
          onChange={(e) => setamountinwords(e.target.value)}
        />
      </div>

      {/* Thank you */}
      <div className="mt-6 text-sm font-bold leading-snug">
        <p>Thank you!</p>
        <p>It’s been a pleasure hosting you!</p>
      </div>

      {/* Cancellation + Payment */}
      <div className="mt-10 grid grid-cols-2 gap-10">
        <div>
          <p className={label}>Cancellation Policy</p>
          <ul className="mt-4 text-xs leading-relaxed text-[#3b413a] list-disc pl-5 space-y-1 w-4/5">
            <li>
              30 days prior to the check-in date, 100% payment refundable.
            </li>
            <li>
              If cancellation is not made before the 30 days then advance amount
              will be forfeited.
            </li>
          </ul>
        </div>
        <div>
          <p className={label}>Payment Info</p>
          <div className="mt-4 text-xs leading-relaxed text-[#3b413a] space-y-0.5">
            <p>HDFC Bank</p>
            <p>Ac NO.: 50200077506738</p>
            <p>Ac Name: ZORBA STAYS</p>
            <p>IFSC: HDFC0000101</p>
            <p>Branch: RAJKOT - YAGNIK ROAD - GUJARAT</p>
            <p>Account Type: CURRENT</p>
          </div>
          <div className="mt-4 flex items-end gap-3">
            <img
              src="https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public"
              alt="UPI QR Code - Zorba Stays"
              className="w-24 h-24 object-contain"
            />
            <div className="text-xs text-[#3b413a] leading-relaxed">
              <p>Name: ZORBA STAYS</p>
              <p>UPI ID: 8866262368@hdfcbank</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-14 flex items-center gap-3">
        <div className="flex-1 border-t border-[#1c2420]"></div>
        <img
          src="https://dash.zorbastays.com/assets/zorba_logo.webp"
          alt="Zorba Stays"
          className="w-5 h-5 object-contain"
        />
        <div className="flex-1 border-t border-[#1c2420]"></div>
      </div>
      <div className="mt-4 flex justify-between text-[10px] text-[#3b413a]">
        <span>@zorba_stays</span>
        <span>www.zorbastays.com</span>
      </div>

      <div className="flex justify-center gap-4 mt-10 mb-4 no-print">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm ${
            isSubmitting ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>
        <button
          onClick={handleCancel}
          className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default CashbackElementore;
// import React, { useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { GoPlusCircle } from "react-icons/go";
// import { FiMinusCircle } from "react-icons/fi";
// import { API_URL } from "../../Config";

// const emptyRow = () => ({
//   from: "",
//   to: "",
//   particulars: "",
//   nights: "",
//   amount: "",
//   cashback: "",
// });

// const CashbackElementore = () => {
//   const navigate = useNavigate();

//   const [invoiceno, setinvoiceno] = useState("");
//   const [invoicedate, setinvoicedate] = useState("");
//   const [guestname, setguestname] = useState("");
//   const [amountinwords, setamountinwords] = useState("");
//   const [total, setTotal] = useState("");
//   const [rows, setRows] = useState([emptyRow()]);

//   const [isSubmitting, setIsSubmitting] = useState(false);

//   const formatDateToDisplay = (date) => {
//     if (!date) return "";
//     const [year, month, day] = date.split("-");
//     if (!year || !month || !day) return date;
//     return `${day}-${month}-${year}`;
//   };

//   const handleFocus = (e) => (e.target.type = "date");
//   const handleBlur = (e) => (e.target.type = "text");

//   const addRow = () => {
//     if (rows.length < 7) {
//       setRows([...rows, emptyRow()]);
//     }
//   };

//   const removeRow = (indexToRemove) => {
//     setRows(rows.filter((_, index) => index !== indexToRemove));
//   };

//   const handleInputChange = (index, field, value) => {
//     const updatedRows = [...rows];
//     updatedRows[index][field] = value;
//     setRows(updatedRows);
//   };

//   const handleCancel = () => {
//     const confirmed = window.confirm("Sure want to discard changes?");
//     if (confirmed) {
//       navigate("/Bills");
//     }
//   };

//   const handleSubmit = async () => {
//     if (!invoiceno || !invoicedate || !guestname) {
//       alert("Invoice No, Invoice Date and Guest Name are required.");
//       return;
//     }
//     if (rows.length === 0) {
//       alert("Cashback invoice must contain at least one item.");
//       return;
//     }

//     const payload = {
//       invoiceno,
//       invoicedate,
//       guestname,
//       amountinwords,
//       total: total || 0,
//       items: rows.map((row) => ({
//         particulars: row.particulars,
//         checkin: row.from || null,
//         checkout: row.to || null,
//         nights: row.nights || 0,
//         amount: row.amount || 0,
//         cashback: row.cashback || 0,
//       })),
//     };

//     try {
//       setIsSubmitting(true);
//       const res = await fetch(`${API_URL}/billing/cashback-invoice`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });

//       const data = await res.json();

//       if (!res.ok || !data.success) {
//         alert(data.message || "Failed to save cashback invoice.");
//         return;
//       }

//       alert("Cashback invoice saved successfully!");
//       navigate("/bills");
//     } catch (err) {
//       console.error("Error saving cashback invoice:", err);
//       alert("Something went wrong while saving the cashback invoice.");
//     } finally {
//       setIsSubmitting(false);
//     }
//   };

//   return (
//     <div className="max-w-5xl mx-auto p-10 bg-white text-gray-800 font-sans print:p-0">
//       <style>{`
//         @media print {
//           @page { margin: 0; }
//           body { margin: 1.6cm; }
//           .no-print { display: none; }
//         }
//       `}</style>

//       {/* Header */}
//       <div className="flex justify-between items-start mb-6">
//         <div>
//           <h1 className="text-4xl font-bold mb-2">Cashback Invoice</h1>
//           <br />
//           <br />
//           <h1 className="nt-seawave text-4xl mb-2 text-black">ZORBA STAYS</h1>
//           <p className="text-sm">
//             306, 7 - Business Centrum, <br /> Amin Marg, Rajkot, 360001
//           </p>
//           {/* <p className="text-sm">GSTIN No: 24AADFZ2976K1ZO</p> */}
//         </div>
//         <div className="w-24 h-24">
//           <img
//             src="https://zorbastays.com/android-chrome-512x512.png"
//             alt="Zorba Stays Logo"
//             className="w-full h-full object-contain"
//           />
//         </div>
//       </div>

//       <div className="border-t border-black mb-6"></div>

//       {/* Invoice Meta */}
//       <div className="flex gap-16 mb-8">
//         <div>
//           <p className="text-xs font-bold uppercase">Invoice No.</p>
//           <input
//             type="text"
//             className="w-20 outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
//             placeholder="00"
//             value={invoiceno}
//             onChange={(e) => setinvoiceno(e.target.value)}
//           />
//         </div>
//         <div>
//           <p className="text-xs font-bold uppercase">Invoice Date</p>
//           <input
//             type="text"
//             value={formatDateToDisplay(invoicedate)}
//             onChange={(e) => setinvoicedate(e.target.value)}
//             className="outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//             placeholder="DD-MM-YYYY"
//             onFocus={handleFocus}
//             onBlur={handleBlur}
//           />
//         </div>
//       </div>

//       <div className="mb-8">
//         <p className="font-bold">Invoice To:-</p>
//         <input
//           type="text"
//           className="w-full text-lg outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//           placeholder="Guest Name"
//           value={guestname}
//           onChange={(e) => setguestname(e.target.value)}
//         />
//       </div>

//       {/* Table Design */}
//       <table className="w-full border-collapse border-t border-b border-black text-center text-sm">
//         <thead>
//           <tr className="uppercase text-xs font-bold border-b border-black">
//             <th className="py-3 border-black w-[60px]">Sr. No.</th>
//             <th className="py-3">Particulars</th>
//             <th className="py-3">Period of stay</th>
//             <th className="py-3">No. Nights</th>
//             <th className="py-3">Bill Amount</th>
//             <th className="py-3">Cashback</th>
//           </tr>
//         </thead>
//         <tbody>
//           {rows.map((row, index) => (
//             <tr key={index} className="border-b border-black">
//               <td className="py-3 border-black">{index + 1}</td>
//               <td className="py-3 px-2">
//                 <input
//                   className="w-full text-center outline-none"
//                   placeholder="Villa Name"
//                   value={row.particulars}
//                   onChange={(e) =>
//                     handleInputChange(index, "particulars", e.target.value)
//                   }
//                 />
//               </td>
//               <td className="py-3 px-2">
//                 <div className="flex justify-center items-center gap-1">
//                   <input
//                     type="text"
//                     className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//                     placeholder="DD-MM-YYYY"
//                     value={formatDateToDisplay(row.from)}
//                     onChange={(e) =>
//                       handleInputChange(index, "from", e.target.value)
//                     }
//                     onFocus={handleFocus}
//                     onBlur={handleBlur}
//                   />
//                   <span>to</span>
//                   <input
//                     type="text"
//                     className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none"
//                     placeholder="DD-MM-YYYY"
//                     value={formatDateToDisplay(row.to)}
//                     onChange={(e) =>
//                       handleInputChange(index, "to", e.target.value)
//                     }
//                     onFocus={handleFocus}
//                     onBlur={handleBlur}
//                   />
//                 </div>
//               </td>
//               <td className="py-3">
//                 <input
//                   type="text"
//                   className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
//                   placeholder="00"
//                   value={row.nights}
//                   onChange={(e) =>
//                     handleInputChange(index, "nights", e.target.value)
//                   }
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   type="text"
//                   className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
//                   placeholder="0000"
//                   value={row.amount}
//                   onChange={(e) =>
//                     handleInputChange(index, "amount", e.target.value)
//                   }
//                 />
//               </td>
//               <td className="py-3">
//                 <input
//                   type="text"
//                   className="w-full text-center outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
//                   placeholder="0000"
//                   value={row.cashback}
//                   onChange={(e) =>
//                     handleInputChange(index, "cashback", e.target.value)
//                   }
//                 />
//               </td>
//               <td className="py-3 no-print">
//                 {index >= 1 && (
//                   <button
//                     onClick={() => removeRow(index)}
//                     className="px-2 py-1 hover:scale-110"
//                   >
//                     <FiMinusCircle size={18} />
//                   </button>
//                 )}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       {/* Add Button */}
//       <div className="my-2 no-print">
//         <button
//           onClick={addRow}
//           disabled={rows.length >= 7}
//           className={`px-4 py-2 ${rows.length >= 7 ? "opacity-50 cursor-not-allowed" : "hover:scale-110"}`}
//         >
//           <GoPlusCircle size={22} />
//         </button>
//       </div>

//       {/* Totals */}
//       <div className="flex justify-between items-start mt-6 px-2">
//         <div className="w-1/2">
//           <p className="font-bold mb-1">In Words:</p>
//           <input
//             type="text"
//             className="w-full uppercase outline-none border-t-0 border-l-0 border-r-0 border-b border-black focus:ring-0 focus:border-black"
//             placeholder="Enter amount in words"
//             value={amountinwords}
//             onChange={(e) => setamountinwords(e.target.value)}
//           />
//         </div>
//         <div className="w-48">
//           <div className="flex justify-between py-1 font-bold border-b border-black">
//             <span>Total</span>
//             <input
//               type="number"
//               className="w-24 text-right outline-none border-none bg-transparent focus:ring-0 focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
//               placeholder="0000"
//               value={total}
//               onChange={(e) => setTotal(e.target.value)}
//             />
//           </div>
//         </div>
//       </div>

//       {/* Payment Info */}
//       <div className="mt-12 pt-6 border-t border-black">
//         <div className="flex justify-between items-start gap-8">
//           {/* Left: Bank details */}
//           <div className="flex-1">
//             <h2 className="text-2xl font-bold text-[#22261f] mb-2">
//               Payment Info
//             </h2>
//             <div className="text-sm text-gray-700 leading-relaxed space-y-0.5">
//               <p>HDFC Bank</p>
//               <p>Ac NO.: 50200077506738</p>
//               <p>Ac Name: ZORBA STAYS</p>
//               <p>IFSC: HDFC0000101</p>
//               <p>Branch: RAJKOT - YAGNIK ROAD - GUJARAT</p>
//               <p>Account Type: CURRENT</p>
//             </div>
//           </div>

//           {/* Right: QR code + UPI */}
//           <div className="flex flex-col items-end">
//             <img
//               src="https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public"
//               alt="UPI QR Code - Zorba Stays"
//               className="w-40 h-40 object-contain"
//             />
//             <p className="text-sm font-bold text-[#22261f] mt-2 text-right">
//               Name: ZORBA STAYS
//             </p>
//             <p className="text-sm text-gray-700 text-right">
//               UPI ID: 8866262368@hdfcbank
//             </p>
//           </div>
//         </div>
//       </div>
//       {/* Footer */}
//       <div className="mt-20 text-center text-xs pt-8">
//         <p>www.zorbastays.com | Contact: +91 63552 31066, +91 98750 28066</p>
//       </div>

//       <div className="flex justify-center gap-4 mt-8 mb-10 no-print">
//         <button
//           onClick={handleSubmit}
//           disabled={isSubmitting}
//           className={`px-6 z-30 py-2 bg-green-900 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-green-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm ${
//             isSubmitting ? "opacity-60 cursor-not-allowed" : ""
//           }`}
//         >
//           {isSubmitting ? "Saving..." : "Save"}
//         </button>
//         <button
//           onClick={handleCancel}
//           className="px-6 z-30 py-2 bg-gray-800 rounded-lg text-center text-white relative hover:scale-95 after:-z-20 after:absolute after:h-1 after:w-1 after:bg-gray-700 after:left-5 overflow-hidden after:bottom-0 after:translate-y-full after:rounded-md after:hover:scale-[300] after:hover:transition-all after:hover:duration-700 after:transition-all after:duration-700 transition-all duration-700 text-sm"
//         >
//           Cancel
//         </button>
//       </div>
//     </div>
//   );
// };

// export default CashbackElementore;
